// useStatisticalAnalysis.ts
import { useMemo } from 'react';
import { ITeamWeekRank } from 'types';

interface StatisticalAnalysis {
  mean: number;
  reducedMean: number;
  median: number;
  stdDev: number;
  max: number;
  min: number;
  range: number;
  variance: number;
  sum: number;
  count: number;
}

export const useStatisticalAnalysis = (data: ITeamWeekRank[]): StatisticalAnalysis | null => {
  return useMemo(() => {
    // ✅ Early return for empty/invalid data
    if (!data || data.length === 0) {
      return null;
    }

    // ✅ Filter out bye weeks
    const validData = data.filter((item) => !item.isByeWeek);

    // ✅ Return null if no valid data after filtering
    if (validData.length === 0) {
      return null;
    }

    // ✅ Extract values for calculations
    const values = validData.map((item) => item.value);
    const count = values.length;

    // ✅ Basic calculations
    const sum = values.reduce((acc, curr) => acc + curr, 0);
    const mean = sum / count;

    // ✅ Sort without mutating original
    const sortedValues = [...values].sort((a, b) => a - b);

    // ✅ Correct median calculation
    const median =
      count % 2 === 0
        ? (sortedValues[count / 2 - 1] + sortedValues[count / 2]) / 2
        : sortedValues[Math.floor(count / 2)];

    // ✅ Variance and standard deviation
    const variance = values.reduce((acc, curr) => acc + Math.pow(curr - mean, 2), 0) / count;
    const stdDev = Math.sqrt(variance);

    // ✅ Min, max, range
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min;

    // ✅ Reduced mean (trimmed mean)
    let reducedMean = mean; // Default to regular mean
    if (count > 2) {
      // Remove highest and lowest values for trimmed mean
      const trimmedValues = sortedValues.slice(1, -1);
      reducedMean = trimmedValues.reduce((acc, curr) => acc + curr, 0) / trimmedValues.length;
    }

    return {
      mean,
      reducedMean,
      median,
      stdDev,
      max,
      min,
      range,
      variance,
      sum,
      count,
    };
  }, [data]);
};

export default useStatisticalAnalysis;
